import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext.jsx";
import { completeAdminWalkthrough, getAdminUser } from "@/api/admin.api.js";
import { completeCustomerWalkthrough, getUser } from "@/api/customer.api.js";
import { ADMIN_PROFILE_ROUTE, MAIN_ROUTE, ROLES } from "@/utils/constants.js";
import { ADMIN_PROFILE_DEFAULT_SECTION } from "@/utils/profileMenus.js";
import Walkthrough from "@/components/Walkthrough.jsx";
import {
    ADMIN_WALKTHROUGH_STEPS,
    CUSTOMER_WALKTHROUGH_STEPS,
    GUEST_WALKTHROUGH_STEPS,
    GUEST_WALKTHROUGH_STORAGE_KEY
} from "@/components/walkthroughSteps.js";

function readGuestWalkthroughCompleted() {
    try {
        return localStorage.getItem(GUEST_WALKTHROUGH_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function writeGuestWalkthroughCompleted() {
    try {
        localStorage.setItem(GUEST_WALKTHROUGH_STORAGE_KEY, "1");
    } catch {
        // ignore quota / private mode
    }
}

/**
 * Owns guest (device), customer, and admin (account) walkthroughs.
 * @returns {{ walkthroughActive: boolean, WalkthroughHost: () => JSX.Element | null }}
 */
export function usePlatformWalkthrough({ loginOpen = false } = {}) {
    const { isAuthenticated, role, token } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isCustomer = isAuthenticated && role === ROLES.CUSTOMER;
    const isAdmin = isAuthenticated && role === ROLES.ADMIN;
    const isGuest = !isAuthenticated;

    const [guestCompleted, setGuestCompleted] = useState(readGuestWalkthroughCompleted);
    const [guestSkippedSession, setGuestSkippedSession] = useState(false);
    const [customerSkippedSession, setCustomerSkippedSession] = useState(false);
    const [adminSkippedSession, setAdminSkippedSession] = useState(false);
    const [guestOpen, setGuestOpen] = useState(false);
    const [customerOpen, setCustomerOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [customerNeedsTour, setCustomerNeedsTour] = useState(false);
    const [adminNeedsTour, setAdminNeedsTour] = useState(false);

    const guestLaunchRef = useRef(false);
    const customerLaunchRef = useRef(false);
    const adminLaunchRef = useRef(false);
    const guestTimerRef = useRef(null);
    const customerTimerRef = useRef(null);
    const adminTimerRef = useRef(null);

    useEffect(() => {
        customerLaunchRef.current = false;
        setCustomerSkippedSession(false);
        setCustomerNeedsTour(false);
        setCustomerOpen(false);

        if (!isCustomer || !token) {
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const user = await getUser();
                if (!cancelled) {
                    setCustomerNeedsTour(user?.walkthroughCompleted !== true);
                }
            } catch {
                if (!cancelled) {
                    setCustomerNeedsTour(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isCustomer, token]);

    useEffect(() => {
        adminLaunchRef.current = false;
        setAdminSkippedSession(false);
        setAdminNeedsTour(false);
        setAdminOpen(false);

        if (!isAdmin || !token) {
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const user = await getAdminUser();
                if (!cancelled) {
                    setAdminNeedsTour(user?.walkthroughCompleted !== true);
                }
            } catch {
                if (!cancelled) {
                    setAdminNeedsTour(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAdmin, token]);

    useEffect(() => {
        if (!isGuest) {
            guestLaunchRef.current = false;
            setGuestOpen(false);
            if (guestTimerRef.current) {
                window.clearTimeout(guestTimerRef.current);
                guestTimerRef.current = null;
            }
        }
    }, [isGuest]);

    useEffect(() => {
        if (guestTimerRef.current) {
            window.clearTimeout(guestTimerRef.current);
            guestTimerRef.current = null;
        }

        const eligible =
            isGuest
            && !guestCompleted
            && !guestSkippedSession
            && !customerOpen
            && !adminOpen
            && !guestLaunchRef.current;

        if (!eligible) {
            return undefined;
        }

        guestLaunchRef.current = true;
        guestTimerRef.current = window.setTimeout(() => {
            setGuestOpen(true);
        }, 800);

        return () => {
            if (guestTimerRef.current) {
                window.clearTimeout(guestTimerRef.current);
                guestTimerRef.current = null;
            }
        };
    }, [isGuest, guestCompleted, guestSkippedSession, customerOpen, adminOpen]);

    useEffect(() => {
        if (guestOpen && location.pathname !== MAIN_ROUTE) {
            navigate(MAIN_ROUTE);
        }
    }, [guestOpen, location.pathname, navigate]);

    useEffect(() => {
        if (customerOpen && location.pathname !== MAIN_ROUTE) {
            navigate(MAIN_ROUTE);
        }
    }, [customerOpen, location.pathname, navigate]);

    useEffect(() => {
        if (customerTimerRef.current) {
            window.clearTimeout(customerTimerRef.current);
            customerTimerRef.current = null;
        }

        const eligible =
            isCustomer
            && customerNeedsTour
            && !customerSkippedSession
            && !guestOpen
            && !adminOpen
            && !customerLaunchRef.current;

        if (!eligible) {
            return undefined;
        }

        customerLaunchRef.current = true;
        customerTimerRef.current = window.setTimeout(() => {
            setGuestOpen(false);
            setCustomerOpen(true);
        }, 600);

        return () => {
            if (customerTimerRef.current) {
                window.clearTimeout(customerTimerRef.current);
                customerTimerRef.current = null;
            }
        };
    }, [
        isCustomer,
        customerNeedsTour,
        customerSkippedSession,
        guestOpen,
        adminOpen
    ]);

    useEffect(() => {
        if (adminTimerRef.current) {
            window.clearTimeout(adminTimerRef.current);
            adminTimerRef.current = null;
        }

        const eligible =
            isAdmin
            && adminNeedsTour
            && !adminSkippedSession
            && !guestOpen
            && !customerOpen
            && !adminLaunchRef.current;

        if (!eligible) {
            return undefined;
        }

        adminLaunchRef.current = true;
        adminTimerRef.current = window.setTimeout(() => {
            setGuestOpen(false);
            setCustomerOpen(false);
            setAdminOpen(true);
        }, 600);

        return () => {
            if (adminTimerRef.current) {
                window.clearTimeout(adminTimerRef.current);
                adminTimerRef.current = null;
            }
        };
    }, [
        isAdmin,
        adminNeedsTour,
        adminSkippedSession,
        guestOpen,
        customerOpen
    ]);

    useEffect(() => {
        if (!adminOpen) {
            return;
        }
        const target = `${ADMIN_PROFILE_ROUTE}?section=${ADMIN_PROFILE_DEFAULT_SECTION}`;
        const current = `${location.pathname}${location.search || ""}`;
        if (current !== target && location.pathname !== ADMIN_PROFILE_ROUTE) {
            navigate(target);
        } else if (location.pathname === ADMIN_PROFILE_ROUTE && !location.search.includes("section=")) {
            navigate(target, { replace: true });
        }
    }, [adminOpen, location.pathname, location.search, navigate]);

    const handleGuestComplete = useCallback(() => {
        writeGuestWalkthroughCompleted();
        setGuestCompleted(true);
        setGuestOpen(false);
    }, []);

    const handleGuestSkip = useCallback(() => {
        setGuestSkippedSession(true);
        setGuestOpen(false);
    }, []);

    const handleCustomerComplete = useCallback(async () => {
        try {
            await completeCustomerWalkthrough();
        } catch {
            // Close locally; next session retries if the API failed
        }
        setCustomerNeedsTour(false);
        setCustomerOpen(false);
    }, []);

    const handleCustomerSkip = useCallback(() => {
        setCustomerSkippedSession(true);
        setCustomerOpen(false);
    }, []);

    const handleAdminComplete = useCallback(async () => {
        try {
            await completeAdminWalkthrough();
        } catch {
            // Close locally; next session retries if the API failed
        }
        setAdminNeedsTour(false);
        setAdminOpen(false);
    }, []);

    const handleAdminSkip = useCallback(() => {
        setAdminSkippedSession(true);
        setAdminOpen(false);
    }, []);

    const showGuest = guestOpen && !loginOpen;
    const showCustomer = customerOpen && !loginOpen;
    const showAdmin = adminOpen && !loginOpen;
    const walkthroughActive = showGuest || showCustomer || showAdmin;

    const WalkthroughHost = useCallback(() => {
        if (showAdmin) {
            return (
                <Walkthrough
                    steps={ADMIN_WALKTHROUGH_STEPS}
                    open
                    onComplete={handleAdminComplete}
                    onSkip={handleAdminSkip}
                />
            );
        }
        if (showCustomer) {
            return (
                <Walkthrough
                    steps={CUSTOMER_WALKTHROUGH_STEPS}
                    open
                    onComplete={handleCustomerComplete}
                    onSkip={handleCustomerSkip}
                />
            );
        }
        if (showGuest) {
            return (
                <Walkthrough
                    steps={GUEST_WALKTHROUGH_STEPS}
                    open
                    onComplete={handleGuestComplete}
                    onSkip={handleGuestSkip}
                />
            );
        }
        return null;
    }, [
        showAdmin,
        showCustomer,
        showGuest,
        handleAdminComplete,
        handleAdminSkip,
        handleCustomerComplete,
        handleCustomerSkip,
        handleGuestComplete,
        handleGuestSkip
    ]);

    return { walkthroughActive, WalkthroughHost };
}
